<template>
  <el-dialog
    :title="isEdit ? '编辑人员' : '新增人员'"
    :visible.sync="dialogVisible"
    width="520px"
    :close-on-click-modal="false"
    append-to-body
    @open="handleOpen"
    @closed="handleClosed"
  >
    <!-- autocomplete="off" 阻止浏览器将此表单识别为登录表单并自动填充账号/密码 -->
    <el-form
      ref="form"
      :model="formData"
      :rules="formRules"
      label-width="90px"
      v-loading="detailLoading"
      autocomplete="off"
    >
      <!-- 登录账号与后端 userCode 一致；编辑时不可改 -->
      <el-form-item label="登录用户名" prop="account">
        <el-input
          v-model="formData.account"
          placeholder="系统登录账号（不可修改）"
          maxlength="36"
          show-word-limit
          clearable
          :disabled="isEdit"
          autocomplete="off"
        ></el-input>
      </el-form-item>

      <el-form-item label="姓名" prop="userName">
        <el-input
          v-model="formData.userName"
          placeholder="请输入姓名"
          maxlength="50"
          show-word-limit
          clearable
          autocomplete="off"
        ></el-input>
      </el-form-item>

      <el-form-item label="手机号" prop="tel">
        <el-input
          v-model="formData.tel"
          placeholder="请输入手机号"
          maxlength="11"
          clearable
          autocomplete="off"
        ></el-input>
      </el-form-item>

      <el-form-item label="登录密码" prop="pwd" v-if="!isEdit">
        <el-input
          v-model="formData.pwd"
          placeholder="不填写则使用默认密码"
          maxlength="20"
          show-password
          clearable
          autocomplete="new-password"
        ></el-input>
      </el-form-item>

      <el-form-item label="身份证号" prop="idNum">
        <el-input
          v-model="formData.idNum"
          placeholder="选填"
          maxlength="18"
          clearable
        ></el-input>
      </el-form-item>

      <el-form-item label="关联岗位" prop="roleCodes">
        <el-select
          v-model="formData.roleCodes"
          multiple
          collapse-tags
          placeholder="请选择岗位（可多选）"
          style="width: 100%"
        >
          <el-option
            v-for="role in deptRoles"
            :key="role.roleCode"
            :label="role.roleName"
            :value="role.roleCode"
          ></el-option>
        </el-select>
        <div class="field-tip">不选择岗位则人员无权限访问系统</div>
      </el-form-item>
    </el-form>

    <div slot="footer">
      <el-button @click="dialogVisible = false">取消</el-button>
      <el-button type="primary" @click="handleSubmit" :loading="submitting">确定</el-button>
    </div>
  </el-dialog>
</template>

<script>
import { userService } from './user.service';

export default {
  name: 'UserDialog',
  props: {
    visible: { type: Boolean, default: false },
    /**
     * 编辑时传入用户编码，新增时为 null。
     * 使用多类型避免 Vue 将 null 与 String 混用时出现非预期合并/残留。
     */
    userCode: { type: [String, Number], default: null }
  },
  data() {
    return {
      formData: {
        userName: '',
        account: '',
        tel: '',
        pwd: '',
        idNum: '',
        roleCodes: []
      },
      deptRoles: [],
      detailLoading: false,
      submitting: false
    };
  },
  computed: {
    dialogVisible: {
      get() { return this.visible; },
      set(val) { this.$emit('update:visible', val); }
    },
    isEdit() { return !!this.userCode; },
    /** 新增时校验登录账号；编辑时账号只读，不参与校验 */
    formRules() {
      const rules = {
        userName: [{ required: true, message: '请输入姓名', trigger: 'blur' }],
        tel: [
          { required: true, message: '请输入手机号', trigger: 'blur' },
          { pattern: /^1[3-9]\d{9}$/, message: '手机号格式不正确', trigger: 'blur' }
        ],
        idNum: [
          {
            pattern: /^[1-9]\d{5}(18|19|20)\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01])\d{3}[\dXx]$/,
            message: '身份证号格式不正确',
            trigger: 'blur'
          }
        ],
        roleCodes: [{ required: true, message: '请选择岗位', trigger: 'change' }]
      };
      if (!this.isEdit) {
        rules.account = [
          { required: true, message: '请输入登录用户名', trigger: 'blur' }
        ];
      }
      return rules;
    }
  },
  methods: {
    handleOpen() {
      // 无论新增还是编辑，先同步清空表单，避免沿用上一次打开的内容；编辑再在下方请求回显
      this.resetForm();
      this.$nextTick(() => {
        this.$refs.form && this.$refs.form.clearValidate();
      });
      this.loadDeptRoles();
      if (this.isEdit) {
        this.loadDetail();
      }
    },
    handleClosed() {
      // 仅清空表单；不要用递增序号使详情请求失效：Element UI 在首次打开时可能出现 open/closed 顺序与预期不一致，误伤首次编辑回显
      this.resetForm();
    },
    /** 清空表单：保持 formData 引用不变，便于 el-form 与表单项状态一致 */
    resetForm() {
      this.formData.userName = '';
      this.formData.account = '';
      this.formData.tel = '';
      this.formData.pwd = '';
      this.formData.idNum = '';
      this.formData.roleCodes = [];
    },
    /** 加载本部门可选岗位 */
    loadDeptRoles() {
      userService.getDeptRoles().then(res => {
        this.deptRoles = res.data || [];
      }).catch(() => {
        this.deptRoles = [];
      });
    },
    /** 编辑模式：加载人员详情回填表单 */
    loadDetail() {
      const requestUserCode = this.userCode;
      if (requestUserCode == null || requestUserCode === '') return;
      this.detailLoading = true;
      userService.getDetail(requestUserCode).then(res => {
        // 弹窗已关、组件已销毁、或已切换到其他人员/新增：不再回填
        if (this._isDestroyed || !this.visible) return;
        if (String(this.userCode) !== String(requestUserCode)) return;
        if (!this.isEdit) return;
        const d = res.data;
        this.formData.userName = d.userName || '';
        // 登录账号与 userCode 一致
        this.formData.account = d.userCode || '';
        this.formData.tel = d.tel || '';
        this.formData.pwd = '';
        this.formData.idNum = d.idNum || '';
        this.formData.roleCodes = (d.roles || []).map(r => r.roleCode);
      }).finally(() => {
        this.detailLoading = false;
      });
    },
    handleSubmit() {
      this.$refs.form.validate(valid => {
        if (!valid) return;
        this.submitting = true;
        const data = { ...this.formData };
        // 编辑时补充 userCode
        if (this.isEdit) data.userCode = this.userCode;

        const request = this.isEdit
          ? userService.updateUser(data)
          : userService.saveUser(data);

        request.then(() => {
          this.$message.success(this.isEdit ? '编辑成功' : '新增成功');
          this.dialogVisible = false;
          this.$emit('success');
        }).finally(() => {
          this.submitting = false;
        });
      });
    }
  }
};
</script>

<style scoped lang="scss">
.field-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
  line-height: 1.4;
}

::v-deep .el-form-item {
  margin-bottom: 18px;
}

::v-deep .el-form-item__error {
  position: relative;
  top: 0;
  padding-top: 2px;
}
</style>
